import React, { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../components/layout/MainLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";
import ProgressNoteList from "./Phase-05/ProgressNote/ProgressNoteList";
import {
    fetchProgressNotes,
    deleteProgressNote,
    clearError,
} from "../store/slices/progressNotesSlice";
import { selectHasPermission } from "../store/slices/authSlice";

const ProgressNotes = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Redux state
    const { progressNotes, isLoading, error } = useSelector(
        (state) => state.progressNotes
    );

    // Permissions
    const canManage = useSelector((state) =>
        selectHasPermission(state, "progress-note-manage")
    );
    const canCreate = useSelector((state) =>
        selectHasPermission(state, "progress-note-create")
    );
    const canEdit = useSelector((state) =>
        selectHasPermission(state, "progress-note-edit")
    );
    const canDelete = useSelector((state) =>
        selectHasPermission(state, "progress-note-delete")
    );

    // Fetch records on mount
    useEffect(() => {
        dispatch(fetchProgressNotes());
    }, [dispatch]);

    // Routing handlers
    const handleAdd = () => navigate("/phase05/progress-notes/add");
    const handleEdit = (id) => navigate(`/phase05/progress-notes/edit/${id}`);

    // Delete handler
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This Progress Note will be deleted permanently!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            const res = await dispatch(deleteProgressNote(id));
            if (res.meta.requestStatus === "fulfilled") {
                Swal.fire("Deleted!", "Progress Note has been removed.", "success");
            } else {
                Swal.fire("Error!", "Failed to delete Progress Note.", "error");
            }
        }
    };

    if (!canManage)
        return (
            <Navigate
                to="/dashboard"
                state={{ error: "You do not have permission to manage Progress Notes." }}
                replace
            />
        );

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <strong>Error:</strong> {JSON.stringify(error)}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => dispatch(clearError())}
                        ></button>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <ProgressNoteList
                                records={progressNotes}
                                onAdd={canCreate ? handleAdd : null}
                                onEdit={canEdit ? handleEdit : null}
                                onDelete={canDelete ? handleDelete : null}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ProgressNotes;
